import {
  AfterViewInit,
  Component,
  ComponentFactoryResolver,
  ElementRef,
  HostBinding,
  HostListener,
  Inject,
  OnDestroy,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { CloseModal } from '@app/core/pcs-modal/modal.actions';
import { getModals } from '@app/core/pcs-modal/modal.reducer';
import { ModalConfig, StoreModalConfig } from '@app/core/pcs-modal/modal-config';
import { ModalHostDirective } from '@app/core/pcs-modal/modal-host.directive';
import { ActiveModal } from '@app/core/pcs-modal/model/active-modal';
import { ModalPayload } from '@app/core/pcs-modal/model/modal-payload';
import { getActiveModals, getInactiveModals } from '@app/core/pcs-modal/util';
import { select, Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

@Component({
  selector: 'pcs-modal-root',
  template: `
      <ng-template pcsModalHost></ng-template>
  `,
  styleUrls: ['./root-modal.component.css']
})
export class RootModalComponent implements AfterViewInit, OnDestroy {
  @ViewChild(ModalHostDirective) modalHost: ModalHostDirective;

  private activeModals: ActiveModal[] = [];
  private subscription: Subscription;

  @HostBinding('class.modal')
  @HostBinding('class.fade')
  @HostBinding('class.show')
  @HostBinding('class.d-block')
  get hasModals() {
    return this.activeModals.length > 0;
  }

  get viewContainerRef(): ViewContainerRef {
    return this.modalHost.viewContainerRef;
  }

  constructor(
    @Inject(ModalConfig) private config: StoreModalConfig,
    private componentFactoryResolver: ComponentFactoryResolver,
    private _elRef: ElementRef<HTMLElement>,
    private store: Store<any>) {
  }

  @HostListener('document:keyup.escape')
  onEscape() {
    this.dispatchCloseModalAction();
  }

  @HostListener('document:click', ['$event'])
  backdropClick($event): void {
    if (this._elRef.nativeElement === $event.target) {
      this.dispatchCloseModalAction();
    }
  }

  ngAfterViewInit() {
    this.subscription = this.store.pipe(
      select(this.config.stateKey),
      distinctUntilChanged(),
      map(getModals)
    ).subscribe((modals: ModalPayload[]) => {
      this.closeInactiveModals(modals);
      this.openActiveModals(modals);
    });
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  private closeInactiveModals(modals: ModalPayload[]) {
    const closedModals = getInactiveModals(this.activeModals, modals);

    closedModals.forEach(closedModal => closedModal.ref.destroy());

    this.activeModals = getActiveModals(this.activeModals, modals);
  }

  private openActiveModals(modals: ModalPayload[]) {
    const newModals = getInactiveModals(modals, this.activeModals);

    newModals.forEach(modal => this.open(modal));
  }

  private open(modal: ModalPayload) {
    const { component, props } = modal;

    const componentFactory = this.componentFactoryResolver.resolveComponentFactory(component);
    const componentRef = this.viewContainerRef.createComponent(componentFactory);
    componentRef.instance.props = props;

    this.activeModals.push({ref: componentRef});
  }

  private dispatchCloseModalAction() {
    if (this.hasModals) {
      this.store.dispatch(new CloseModal());
    }
  }
}
